import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useClientAuth } from '../context/ClientAuthContext';
import { Phone, Lock, User, ArrowLeft, Loader2, Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { CURRENCIES } from '../lib/currency';

export default function ClientAuth() {
  const [mode, setMode] = useState('login');
  const [name, setName] = useState('');
  const [currency, setCurrency] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login, register } = useClientAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === 'register') {
        await register(name.trim(), whatsapp.trim(), password, currency);
        localStorage.setItem('rtr_currency', currency);
        toast.success('Account created! Welcome aboard.');
      } else {
        await login(whatsapp.trim(), password);
        toast.success('Welcome back!');
      }
      navigate('/sourcing');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-950 via-blue-900 to-blue-950 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Link to="/sourcing" className="inline-flex items-center gap-2 text-blue-300 hover:text-white mb-6 text-sm transition-colors" data-testid="auth-back-link">
          <ArrowLeft className="w-4 h-4" /> Back to Sourcing Portal
        </Link>

        <div className="bg-white rounded-2xl shadow-2xl p-8" data-testid="client-auth-card">
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold text-gray-900">
              {mode === 'login' ? 'Client Login' : 'Create Account'}
            </h1>
            <p className="text-gray-500 text-sm mt-1">
              {mode === 'login' ? 'Sign in with your WhatsApp number' : 'Register to start sourcing from China'}
            </p>
          </div>

          {/* Tabs */}
          <div className="flex mb-6 bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setMode('login')}
              className={`flex-1 py-2 rounded-md text-sm font-semibold transition-all ${mode === 'login' ? 'bg-white shadow text-blue-900' : 'text-gray-500'}`}
              data-testid="auth-tab-login"
            >
              Login
            </button>
            <button
              onClick={() => setMode('register')}
              className={`flex-1 py-2 rounded-md text-sm font-semibold transition-all ${mode === 'register' ? 'bg-white shadow text-blue-900' : 'text-gray-500'}`}
              data-testid="auth-tab-register"
            >
              Register
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === 'register' && (
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Full Name</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text" value={name} onChange={(e) => setName(e.target.value)}
                    placeholder="Your full name"
                    className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl text-sm focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500/20"
                    required data-testid="auth-name-input"
                  />
                </div>
              </div>
            )}

            {mode === 'register' && (
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Preferred Currency <span className="text-orange-600">*</span></label>
                <select value={currency} onChange={(e) => setCurrency(e.target.value)} required
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm bg-white focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500/20"
                  data-testid="auth-currency-select">
                  <option value="">Select the currency you pay in...</option>
                  {CURRENCIES.map((c) => <option key={c.code} value={c.code}>{c.symbol} {c.code} — {c.name}</option>)}
                </select>
                <p className="text-[11px] text-gray-400 mt-1">All prices will be shown in RMB and your currency (live rate).</p>
              </div>
            )}

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">WhatsApp Number</label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="tel" value={whatsapp} onChange={(e) => setWhatsapp(e.target.value)}
                  placeholder="+233 544 205 844"
                  className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl text-sm focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500/20"
                  required data-testid="auth-whatsapp-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type={showPw ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder={mode === 'register' ? 'Min 6 characters' : 'Your password'}
                  className="w-full pl-10 pr-10 py-3 border border-gray-200 rounded-xl text-sm focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500/20"
                  required minLength={6} data-testid="auth-password-input"
                />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button type="submit" disabled={loading}
              className="w-full bg-orange-600 hover:bg-orange-700 text-white font-bold py-3 rounded-xl shadow-md"
              data-testid="auth-submit-btn">
              {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              {mode === 'login' ? 'Sign In' : 'Create Account'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
